/**
 * Bridges the native application menu into the renderer (CH-01).
 *
 * Main process menu invocation -> `menu:command` IPC -> here -> the shared app
 * command router. Clicks and accelerator fallbacks carry distinct sources so
 * the router can apply the stale-accelerator and full-eligibility rules only
 * to the latter.
 *
 * Mounted once, from MainShell.
 */

import { useEffect } from 'react';
import {
  runAppCommand,
  type RunAppCommandResult,
} from '../../features/keybindings/dispatch/runAppCommand';

export type MenuCommandPayload = {
  readonly commandId: string;
  readonly source: 'click' | 'accelerator';
  readonly accelerator?: string;
};

type AppCommandRunner = (
  commandId: string,
  options: {
    readonly source: 'menu-click' | 'menu-accelerator';
    readonly accelerator?: string;
  },
) => RunAppCommandResult;

export function routeAppMenuCommand(
  payload: MenuCommandPayload,
  runner: AppCommandRunner = runAppCommand,
): RunAppCommandResult {
  return runner(payload.commandId, {
    source: payload.source === 'accelerator' ? 'menu-accelerator' : 'menu-click',
    ...(payload.accelerator ? { accelerator: payload.accelerator } : {}),
  });
}

export function useAppMenuCommands(): void {
  useEffect(() => {
    const api = window.electronAPI?.appMenu;
    if (!api?.onCommand) return;

    return api.onCommand((payload) => {
      // Command ids are static app strings (no user data), so they are safe to
      // report as a low-cardinality analytics property.
      window.electronAPI?.analytics
        ?.track('menu:command', {
          commandId: payload.commandId,
          source: payload.source,
        })
        .catch(() => {});

      routeAppMenuCommand(payload);
    });
  }, []);
}
